import { useSyncExternalStore } from 'react';

export type SpecLaunchState = 'off' | 'read_only' | 'on';

type Listener = () => void;

/**
 * Launch state the sidebar can read without importing the Spec runtime.
 *
 * `__root.tsx` keeps the chat runtime behind `React.lazy`. If AppSidebar
 * imported that module for nav visibility, every page would pay for
 * `@assistant-ui/react` in the entry chunk again. This store is the thin
 * publish channel: the lazy runtime writes here after bootstrap; the sidebar
 * only subscribes.
 *
 * The last answer per workspace is remembered, because the alternative is
 * worse than a stale nav: starting at `off` meant the Spec entries were hidden
 * on every load until bootstrap replied, then appeared and shoved the rest of
 * the sidebar down. A remembered answer is right on first paint, and a wrong
 * one is corrected within the same second by the bootstrap that follows.
 */

const KEY_PREFIX = 'parcel:spec-launch';

let launchState: SpecLaunchState = 'off';
let knownFor: string | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

function storage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isLaunchState(value: unknown): value is SpecLaunchState {
  return value === 'off' || value === 'read_only' || value === 'on';
}

function remembered(workspaceId: string): SpecLaunchState | null {
  try {
    const raw = storage()?.getItem(`${KEY_PREFIX}:${workspaceId}`);
    return isLaunchState(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function getSpecLaunchState(): SpecLaunchState {
  return launchState;
}

/**
 * What this workspace answered last time, applied before bootstrap replies.
 *
 * A workspace nobody has visited falls back to `off`: showing entries that turn
 * out not to exist is worse than showing them a moment late.
 */
export function primeSpecLaunchState(workspaceId: string | null | undefined): void {
  if (!workspaceId) {
    knownFor = null;
    if (launchState !== 'off') {
      launchState = 'off';
      notify();
    }
    return;
  }
  if (knownFor === workspaceId) return;
  knownFor = workspaceId;
  const next = remembered(workspaceId) ?? 'off';
  if (launchState === next) return;
  launchState = next;
  notify();
}

export function setSpecLaunchState(
  next: SpecLaunchState,
  workspaceId?: string | null | undefined,
): void {
  if (workspaceId) {
    knownFor = workspaceId;
    try {
      storage()?.setItem(`${KEY_PREFIX}:${workspaceId}`, next);
    } catch {
      // Remembering is an optimization; a full or locked store must not break the nav.
    }
  }
  if (launchState === next) return;
  launchState = next;
  notify();
}

export function subscribeSpecLaunchState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSpecLaunchState(): SpecLaunchState {
  return useSyncExternalStore(subscribeSpecLaunchState, getSpecLaunchState, () => 'off');
}
